import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Bundle Group — orchestrates multiple Bundle Items, syncs variant dropdowns
 * with the main product variant picker, and recomputes pricing live.
 *
 * This component owns the bundle SELECTION UI only. It does NOT write to the
 * cart. The single owner of the cart-write path is assets/bundle-cart.js, which
 * reads the active tier's variant selections at add-to-cart time. See that file
 * for the property contract (product-type / _bundle_id / Gift Included).
 *
 * The Liquid render establishes structure; this component owns runtime state.
 *
 * @typedef {{ id: number, title: string, price: number, compare_at_price: number,
 *             available: boolean, option1: string|null, option2: string|null,
 *             option3: string|null, featured_image: string, swatch_image: string }} VariantRow
 */
class BundleGroupComponent extends Component {
  /** @type {VariantRow[]} */
  #variants = [];

  /** @type {Map<number, VariantRow>} */
  #variantById = new Map();

  /**
   * P7 — ordered, in-stock, deduplicated slot-default candidates. Rendered by
   * blocks/bundle-group.liquid from product.metafields.custom.variant_sales_rank
   * when that exists, then admin order. Never contains a sold-out variant.
   * @type {number[]}
   */
  #candidates = [];

  /** @type {HTMLFormElement | null} */
  #form = null;

  /** @type {string} */
  #moneyFormat = '{{amount}}';

  /** @type {string} */
  #currency = 'USD';

  /** @type {boolean} */
  #hasVariants = false;

  /**
   * When true, a pending main-picker variant:update was triggered by US (a bundle
   * slot change, to update the featured media) — so #onMainVariantUpdate must NOT
   * mirror that value back into the other slots. Keeps slots independent while
   * still driving Horizon's native variant→media flow.
   * @type {boolean}
   */
  #suppressSlotMirror = false;

  /**
   * The shopper's picks across the WHOLE card, most-recent-first, deduped.
   * This is the cross-tier memory: every tier seeds from it, so a character chosen on
   * any tier follows the shopper to every other tier, in both directions.
   *
   * ⚠️ An earlier build kept only the single last id and seeded only rows the shopper had
   * not touched. That fixed buy-1 -> buy-2 and nothing else: picking on buy 2 and going
   * BACK to buy 1 still showed the stale character, because buy 1 counted as "curated".
   * Chris, Loom de668065: "it always has to be something that gets updated, not just when
   * they go from the buy one to something higher, but also for the other higher options too."
   */
  #recentPicks = [];


  /** @type {AbortController} */
  #abort = new AbortController();

  connectedCallback() {
    document.addEventListener('variant:update', this.#restoreStockLine);
    document.addEventListener('variant:update', this.#restoreStickyPile);
    super.connectedCallback();

    // --- Read JSON variant catalogue ---
    const variantsEl = this.querySelector('script[data-bundle-variants]');
    try {
      this.#variants = variantsEl ? JSON.parse(variantsEl.textContent || '[]') : [];
    } catch {
      this.#variants = [];
    }
    this.#variantById = new Map(this.#variants.map((v) => [Number(v.id), v]));

    // --- P7 slot-default candidates -------------------------------------
    // Read the server-rendered order; fall back to the in-stock variants of the
    // JSON catalogue so this never depends on the attribute being present.
    this.#candidates = String(this.dataset.bundleCandidates || '')
      .split(',')
      .map((raw) => Number(raw.trim()))
      .filter((id) => Number.isFinite(id) && id > 0 && this.#variantById.get(id)?.available);
    if (!this.#candidates.length) {
      this.#candidates = this.#variants.filter((v) => v.available).map((v) => Number(v.id));
    }

    this.#moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    this.#currency = ((window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD').toUpperCase();
    this.#hasVariants = this.dataset.hasVariants === 'true';

    // --- Locate the product form so we can read the selected variant id and
    // update the add-to-cart label. Cart writes are owned entirely by
    // assets/bundle-cart.js, which reads our active tier at submit time — we no
    // longer hijack the form's submit here. ---
    const formId = this.dataset.productFormId;
    if (formId) {
      const form = document.getElementById(formId);
      if (form instanceof HTMLFormElement) {
        this.#form = form;
      }
    }

    const { signal } = this.#abort;

    // --- Wire delegated change handler (radios + selects) ---
    this.addEventListener('change', this.#onChange, { signal });

    // --- P8: delegated click handler for the character pick row ---
    this.addEventListener('click', this.#onSwatchClick, { signal });

    // --- Watch the main variant picker so dropdowns can mirror it ---
    const section = this.closest('.shopify-section') || this.closest('section') || document;
    section.addEventListener(ThemeEvents.variantUpdate, this.#onMainVariantUpdate, { signal });

    // --- Make sure exactly one tier starts as selected ---
    this.#ensureSelectedTier();
    this.#syncSelectedClass();
    this.#placeStockAlert();
    // P8: normalise every pick row from its server-rendered state (badges,
    // aria-pressed, counter, hidden-input order) before anything reads prices.
    this.#syncAllPickRows();
    this.#renderAllPrices();
    this.#updateAddToCartText();
    this.#updateAddToCartState();
    this.#broadcastSubtotal();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort.abort();
  }

  // -----------------------------------------------------------------
  // Event handlers
  // -----------------------------------------------------------------

  /** @param {Event} event */
  #onChange = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (!target) return;

    if (target.matches('[data-bundle-radio]')) {
      this.#syncSelectedClass();

      // ── CARRY THE SHOPPER'S FAVOURITE ACROSS TIERS ────────────────────────────
      // Chris, 2026-09-10 (Loom c6318d5b): "if I go to the buy one option and I pick this
      // character, now if I go to the buy 2 get 20% off option it still has these two items
      // saved, which isn't ideal. I would more so want to have this character as well as the
      // top seller… if people are hesitating and we know this is their favourite, I still
      // want to keep their favourite as a selection and also make the safest bet."
      //
      // #applyPickRowDefaults already does exactly this — it seeds a row as
      // [firstId, ...best sellers] — it was simply never called on a tier switch, so each
      // tier kept whatever it was last left with. It also already protects a row the shopper
      // curated HERSELF (`dataset.userTouched`), where it only guarantees the favourite is
      // present rather than wiping her work. That distinction is what makes this safe to call
      // on every switch: stale server-rendered defaults get re-seeded, deliberate picks do not.
      const switchedTier = this.#getActiveTier();
      const switchedRow = switchedTier && this.#pickRowOf(switchedTier);
      if (switchedRow) {
        if (this.#recentPicks.length) this.#seedRowFromRecent(switchedRow);
        else {
          const seed = this.#currentPickerVariantId();
          if (seed) this.#applyPickRowDefaults(switchedRow, seed);
        }
      }

      this.#renderAllPrices();
      this.#updateAddToCartText();
      this.#updateAddToCartState();
      // ⚠️ THE PILL LIVES INSIDE THE SELECTED TIER'S PICK ROW. #placeStockAlert() appends
      // the single <t1-stock-alert> into whichever row is active, so on a tier switch it is
      // still parented to the PREVIOUS tier's row — which .bundle-tier__variants has just
      // collapsed to max-height:0. Result: the scarcity line silently vanishes on BUY 1 and
      // never appears on BUY 3, and only comes back if the shopper unselects and reselects a
      // character (the only other path that calls this). Reported by Chris on video.
      this.#placeStockAlert();
      this.#broadcastSubtotal();
      return;
    }

    if (target.matches('[data-bundle-variant-select]')) {
      // P8: a hidden input owned by a pick row. The row already wrote every
      // slot; this event exists only so pricing / ATC label / subtotal run
      // through the exact path they always did.
      //
      // ⚠️ Deliberately does NOT drive the main variant picker, unlike the
      // <select> path below. With pick-4-of-6 a shopper taps several swatches
      // in a row, and each sync fires a section fetch and jumps the gallery —
      // the slowness the contract forbids and the friction Loom 4 named. The
      // swatch image already shows which character is picked, which is the
      // whole reason the dropdown thumbnail was proposed in the first place.
      const pickRow = target.closest('[data-bundle-pick]');
      if (pickRow) {
        const pickTier = target.closest('[data-bundle-item]');
        if (pickTier) this.#renderTierPrice(pickTier);
        if (this.#isTierActive(/** @type {HTMLElement} */ (pickTier))) {
          this.#updateAddToCartText();
          this.#broadcastSubtotal();
        }
        this.#updateAddToCartState();
        return;
      }

      // Remember the user has overridden this slot so future main-variant
      // changes don't trample their choice.
      target.dataset.userSet = 'true';
      // Drive Horizon's native variant picker so the featured media gallery
      // updates to this slot's variant (media-gallery listens to variant:update).
      // Guard the resulting variant:update so #onMainVariantUpdate does NOT mirror
      // this value into sibling slots — that keeps each slot independent. If the
      // picker didn't actually move (variant already current, no variant:update
      // will fire), clear the guard immediately so it can't get stuck.
      this.#suppressSlotMirror = true;
      const moved = this.#syncMainPickerFromVariant(Number(target.value));
      if (!moved) this.#suppressSlotMirror = false;
      const tier = target.closest('[data-bundle-item]');
      if (tier) this.#renderTierPrice(tier);
      if (tier && tier.contains(this.#getCheckedRadio()?.parentElement || null)) {
        this.#updateAddToCartText();
        this.#broadcastSubtotal();
      } else if (this.#isTierActive(tier)) {
        this.#updateAddToCartText();
        this.#broadcastSubtotal();
      }
    }
  };

  // -----------------------------------------------------------------
  // P8 — the character pick row
  // -----------------------------------------------------------------

  /**
   * Tap an unselected character -> it joins the selection. Tap a selected one
   * -> it is removed; that is the PRIMARY gesture, not a secondary control.
   * At n/n, tapping an unselected character still works: the least-recently
   * added pick is swapped out in ONE tap. Doing
   * nothing at 4/4 is exactly the friction this phase exists to remove.
   * @param {MouseEvent} event
   */
  #onSwatchClick = (event) => {
    const target = /** @type {HTMLElement|null} */ (event.target);
    const btn = target?.closest?.('[data-bundle-swatch]');
    if (!(btn instanceof HTMLButtonElement) || !this.contains(btn)) return;

    // ⚠️ The row sits inside <label class="bundle-tier">, which wraps the tier
    // radio. A <button> is interactive content so the label should not forward
    // its activation — but a swatch tap silently switching the tier would put
    // the wrong bundle in the cart, so belt AND braces.
    event.preventDefault();
    event.stopPropagation();

    if (btn.disabled) return;
    const row = btn.closest('[data-bundle-pick]');
    if (!row) return;

    const id = Number(btn.dataset.variantId);
    if (!id || !this.#variantById.has(id)) return;

    const capacity = this.#pickCapacity(row);
    const order = this.#readPick(row);
    const at = order.indexOf(id);

    // ──────────────────────────────────────────────────────────────
    // Chris, 2026-09-10 (Loom 68ebb6a8): "the fact that I can't pick multiple of one
    // character is annoying… I should be able to click one more time on this one, all the
    // way up to like four, and then have four selected."
    //
    // The old rule was: tap a picked character -> REMOVE it. That made a duplicate
    // unreachable by hand, and on a 1-available-variant product it was a trap — the
    // Buy-1-Get-1 tier auto-fills 2x the same figure, but once you deselect one you can
    // never get back to two, because every further tap is read as another removal.
    //
    // New rule, when multi-pick is on:
    //   room left        -> ADD one (works whether or not it is already picked)
    //   full + picked    -> REMOVE one of that character (so it stays reversible)
    //   full + unpicked  -> swap out the weakest default, exactly as before
    //
    // ⚠️ Behind `titan_bundle_multi_pick`, defaulting ON, because he asked for it as a
    // reversible test: "make it so I can revert back, I'm not sure of the UI." Untick the
    // setting in the theme editor and the original behaviour returns with no deploy.
    // ──────────────────────────────────────────────────────────────
    const multiPick = this.dataset.bundleMultiPick !== 'false';
    const owned = order.reduce((n, x) => (x === id ? n + 1 : n), 0);

    let didRemove = false;

    // The count badge is the REMOVE control. Chris asked for an interface where adding and
    // removing are both obvious ("how to add, how to remove, very intuitively"), and at a
    // 38px swatch there is no room for a separate control — the badge is already the only
    // element that appears exactly when a character is picked, and it sits in the octagon's
    // dead corner. Tapping it takes one off; tapping anywhere else on the swatch adds one.
    const hitBadge = !!target?.closest?.('[data-bundle-swatch-badge]');

    if (multiPick && hitBadge && owned > 0) {
      order.splice(order.lastIndexOf(id), 1);
      didRemove = true;
    } else if (multiPick) {
      // ⚠️ TAP ALWAYS ADDS. Chris, Loom 68ebb6a8: "I should be able to click one more time
      // on this one, all the way up to like four, and then have four selected." An earlier
      // build made a tap on a full row REMOVE one, which toggles 3<->4 and makes 4x of a
      // single character unreachable — the exact complaint. When the row is full we evict
      // the oldest pick of a DIFFERENT character, so repeated taps on one figure
      // progressively convert the row to that figure instead of fighting the shopper.
      if (order.length >= capacity) {
        const evictAt = order.findIndex((x) => x !== id);
        if (evictAt === -1) {
          // Every slot is already this character — nothing to convert, so leave it be
          // rather than silently resetting a deliberate 4x pick.
          return;
        }
        const evicted = order.splice(evictAt, 1)[0];
        if (evicted != null) this.#flashSwappedOut(row, evicted);
      }
      order.push(id);
    } else if (!multiPick && at !== -1) {
      order.splice(at, 1);
      didRemove = true;
    } else if (!multiPick && order.length < capacity) {
      order.push(id);
    } else {
      const evicted = order.shift();
      order.push(id);
      if (evicted != null) {
        // ⚠️ NO narration. Chris, 2026-09-10: "don't ever include these lines, people
        // understand that instinctively." A swatch losing its ring IS the message; a
        // sentence restating it is noise, and it was the only thing in the card that
        // wrapped to a second row. The flash stays — that is feedback, not commentary.
        this.#flashSwappedOut(row, evicted);
      }
    }

    // The shopper has curated this row. From here a main-picker change may only
    // guarantee its own character is present; it may never reshuffle the rest.
    row.dataset.userTouched = 'true';
    if (didRemove) {
      // Only forget it once NO tier holds it any more, so removing a duplicate on one tier
      // does not erase a character the shopper still has selected elsewhere.
      if (!this.#anyRowHolds(id)) this.#recentPicks = this.#recentPicks.filter((x) => x !== id);
    } else {
      this.#recentPicks = [id, ...this.#recentPicks.filter((x) => x !== id)].slice(0, 12);
    }
    this.#writePick(row, order);

    // Chris, 2026-09-10: "upon selecting a variant from these menu, the product
    // image should change, just like it's doing right now from the style row."
    // The STYLE row is hidden now, so this row is the ONLY way to move the
    // gallery — and the price, the stock pill and the sticky bar ride the same
    // variant:update. Reuses the exact path the legacy <select> already used.
    //
    // ⚠️ This deliberately reverses the P8 decision not to sync the gallery from
    // a swatch tap. That call was made when the STYLE row still existed and a
    // multi-tap sequence would have jumped the gallery for no reason. With the
    // STYLE row gone, not syncing leaves the shopper with no way to see what
    // they picked.
    //
    // ⚠️ On a multi-pick tier the LAST TAPPED character drives the gallery —
    // a removal falls back to whatever is still selected, so the gallery never
    // shows a character the shopper just took out. Anything else is ambiguous:
    // four picks cannot all be the hero image.
    this.#placeStockAlert();
    this.#renderStockLine();
    // A removal falls back to whatever is still selected, so the gallery never shows a
    // character the shopper just took out; anything else shows what was just added.
    // ⚠️ `didRemove` is set by the branch that ran — it must NOT be re-derived from
    // `at !== -1`, which under multi-pick no longer means "this tap was a removal".
    const showing = didRemove ? order[order.length - 1] : id;
    if (showing) {
      // Picture first and locally — 0 requests, 0 wait. Then the picker sync, which
      // still costs the 774 KB fetch, but only price/stock/ATC are now waiting on it.
      this.#showVariantMedia(Number(showing));
      this.#suppressSlotMirror = true;
      if (!this.#syncMainPickerFromVariant(Number(showing))) this.#suppressSlotMirror = false;
    }
  };

  /**
   * Park the low-stock pill INSIDE the selected tier, directly under its pick row.
   *
   * Chris, 2026-09-10: "put the 'Only 42 left' right below the actual variant from
   * the picker, right now it doesn't seem like they're related."
   *
   * ⚠️ The pill is a sibling BLOCK in block_order, two elements above the tier stack,
   * so nothing on the page tied it to the character being chosen. It is MOVED, not
   * re-rendered: <t1-stock-alert> owns its own inventory logic and its own
   * variant:update listener (assets/t1-rwe-stock-alert.js), and a second copy would
   * drift from the first the moment stock changed.
   * ⚠️ Re-runs on tier change, because only the OPEN tier shows a pick row.
   */
  /**
   * Write the low-stock line from the CURRENT PICKS, locally.
   *
   * Chris's three bugs, all the same root cause: <t1-stock-alert> is server-rendered
   * against `selected_or_first_available_variant` — slot 1 — and only refreshes when
   * Horizon's 774 KB variant fetch lands. So it never named a character, never knew
   * about picks 2-4, and stayed hidden on a first pick until you unselected and
   * re-selected (which forced a second fetch). Rendering it here from the numbers
   * already in data-bundle-variants fixes all three at once, with no network.
   *
   * Copy, chosen by Chris 2026-09-10:
   *   one low   -> "Only 42 Fox figures left in stock"
   *   two+ low  -> "Fox and Bear are running low"   (name at most two)
   * ⚠️ When more than two are low we name the top two by the SAME candidate order that
   *   auto-fills the slots, so the card never contradicts itself about which figure
   *   matters most. Naming all four wraps to two rows, which is the thing being fixed.
   */
  #renderStockLine() {
    const pill = document.querySelector('t1-stock-alert');
    if (!pill) return;
    const tier = this.querySelector('[data-bundle-item]:has(.bundle-tier__radio:checked)')
      || this.querySelector('[data-bundle-item].is-selected');
    const row = tier && this.#pickRowOf(tier);
    if (!row) { pill.hidden = true; return; }


    const threshold = Number(this.dataset.bundleLowThreshold) || 100;
    const picks = this.#readPick(row);
    const low = [];
    for (const id of picks) {
      const v = this.#variantById.get(Number(id));
      // the same gate the Liquid uses: tracked, checkout refuses past 0, and under the bar
      if (!v || !v.inventory_tracked) continue;
      const q = Number(v.inventory_quantity);
      if (!(q > 0 && q < threshold)) continue;
      if (!low.some((x) => x.id === v.id)) low.push(v);
    }
    if (!low.length) { pill.hidden = true; return; }

    // ⚠️ BUILD THE PILL'S INSIDES IF THEY ARE MISSING — do not bail.
    // The block is server-rendered, and when the selected variant is NOT low its Liquid
    // gate emits NOTHING inside <t1-stock-alert>. Its own variant:update handler then
    // morphs that empty markup in when the 774 KB fetch lands. So on a variant change the
    // span we write into can simply cease to exist for the duration of the round trip —
    // which is the lag Chris saw, the same shape as the slow image swap. Owning the markup
    // makes the line appear on the click, and survive the morph either way.
    let text = pill.querySelector('.t1sa__text');
    if (!text) {
      const shell = document.createElement('span');
      shell.className = 't1sa';
      shell.innerHTML =
        '<span class="t1sa__dot" aria-hidden="true"><span class="t1sa__halo"><span></span></span></span>'
        + '<span class="t1sa__text" role="status"></span>';
      pill.replaceChildren(shell);
      text = shell.querySelector('.t1sa__text');
    }

    // rank by the candidate order, so "the two we name" matches "the two we auto-filled"
    const order = this.#candidates ? Array.from(this.#candidates) : [];
    const rank = (v) => { const i = order.indexOf(Number(v.id)); return i < 0 ? 9999 : i; };
    low.sort((a, b) => rank(a) - rank(b));

    const name = (v) => v.short_name || v.title;
    if (!text) return;   // shell could not be built; show nothing rather than an empty box
    text.textContent = low.length === 1
      ? `Only ${low[0].inventory_quantity} ${name(low[0])} figures left in stock`
      : `${name(low[0])} and ${name(low[1])} are running low`;
    pill.hidden = false;
  }

  #placeStockAlert() {
    const pill = document.querySelector('t1-stock-alert');
    if (!pill) return;
    const tier = this.querySelector('[data-bundle-item]:has(.bundle-tier__radio:checked)')
      || this.querySelector('[data-bundle-item].is-selected');
    const row = tier && this.#pickRowOf(tier);
    if (!row) {
      if (pill.dataset.t1saHome === 'tier') pill.classList.remove('t1sa--in-tier');
      return;
    }
    // ⚠️ Moving the pill and re-rendering its TEXT are independent concerns. This used to
    // `return` early when the pill was already in the right row, which meant a tier whose
    // row already held the pill kept the PREVIOUS tier's wording. Always re-render.
    if (!row.contains(pill)) {
      pill.classList.add('t1sa--in-tier');
      pill.dataset.t1saHome = 'tier';
      row.appendChild(pill);
    }
    this.#renderStockLine();
  }

  /** @param {Element} tier @returns {HTMLElement|null} */
  #pickRowOf(tier) {
    return /** @type {HTMLElement|null} */ (tier.querySelector('[data-bundle-pick]'));
  }

  /** @param {Element} row @returns {number} */
  #pickCapacity(row) {
    return Math.max(1, Number(/** @type {HTMLElement} */ (row).dataset.bundlePickCapacity) || 1);
  }

  /** @param {number} id @returns {string} */
  #variantTitle(id) {
    return this.#variantById.get(Number(id))?.title || 'that character';
  }

  /** @param {Element} row @returns {number[]} the ids the swatch row can offer */
  #renderedIds(row) {
    return Array.from(row.querySelectorAll('[data-bundle-swatch]'))
      .filter((btn) => !(/** @type {HTMLButtonElement} */ (btn).disabled))
      .map((btn) => Number(/** @type {HTMLElement} */ (btn).dataset.variantId))
      .filter((id) => Number.isFinite(id) && id > 0);
  }

  /**
   * The current selection, OLDEST FIRST. Recency lives in the DOM
   * (data-bundle-pick-order) rather than in a JS field so it survives a
   * section morph exactly the way data-user-set does on the <select> path.
   * @param {Element} row
   * @returns {number[]}
   */
  #readPick(row) {
    const offered = new Set(this.#renderedIds(row));
    return String(/** @type {HTMLElement} */ (row).dataset.bundlePickOrder || '')
      .split(',')
      .map((raw) => Number(raw.trim()))
      .filter((id) => Number.isFinite(id) && id > 0 && offered.has(id))
      .slice(0, this.#pickCapacity(row));
  }

  /**
   * Write a selection back to the DOM: recency attribute, badges, aria-pressed,
   * the counter line and the hidden inputs.
   *
   * ⚠️ The hidden inputs are the cart contract. bundle-selector's own
   * #getTierVariantIds, and bundle-cart.js's independent scrapeActiveTier, both
   * read `.value` off [data-bundle-variant-select]. They are written in ROW
   * order — the same order blocks/bundle-tier-dynamic.liquid renders them in —
   * so a cart written before hydration and one written after agree.
   *
   * @param {Element} row
   * @param {number[]} order oldest-first
   * @param {boolean} [silent] skip the change event (init only)
   */
  #writePick(row, order, silent = false) {
    const capacity = this.#pickCapacity(row);
    const ids = order.slice(0, capacity);
    /** @type {HTMLElement} */ (row).dataset.bundlePickOrder = ids.join(',');

    /** @type {Map<number, number>} */
    const counts = new Map();
    for (const id of ids) counts.set(id, (counts.get(id) || 0) + 1);

    /** @type {number[]} */
    const rowOrdered = [];
    for (const btn of row.querySelectorAll('[data-bundle-swatch]')) {
      const el = /** @type {HTMLElement} */ (btn);
      const id = Number(el.dataset.variantId);
      const n = counts.get(id) || 0;
      el.setAttribute('aria-pressed', n > 0 ? 'true' : 'false');
      el.classList.toggle('is-picked', n > 0);
      const badge = el.querySelector('[data-bundle-swatch-badge]');
      if (badge) {
        badge.textContent = String(n);
        badge.toggleAttribute('hidden', n === 0);
      }
      for (let i = 0; i < n; i++) rowOrdered.push(id);
    }

    const counter = row.querySelector('[data-bundle-pick-counter]');
    if (counter) counter.textContent = this.#counterText(ids.length, capacity);


    const inputs = /** @type {HTMLInputElement[]} */ (
      Array.from(row.querySelectorAll('[data-bundle-variant-select]'))
    );
    inputs.forEach((input, index) => {
      input.value = rowOrdered[index] != null ? String(rowOrdered[index]) : '';
    });

    // ONE change event for the whole row, on a control #onChange already
    // listens for, so pricing / ATC label / subtotal keep working untouched.
    if (!silent && inputs[0]) {
      inputs[0].dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * House voice: sentence case, a colon, no terminal period; CSS uppercases it.
   * @param {number} chosen @param {number} capacity
   */
  #counterText(chosen, capacity) {
    if (chosen >= capacity) return `Pick ${capacity}: ${capacity} of ${capacity} chosen`;
    return `Pick ${capacity}: choose ${capacity - chosen} more`;
  }

  /** @param {Element} row @param {number} id */
  #flashSwappedOut(row, id) {
    const btn = row.querySelector(`[data-bundle-swatch][data-variant-id="${id}"]`);
    if (!btn) return;
    btn.classList.remove('is-swapped-out');
    // Reflow so the animation restarts when the same swatch is swapped twice.
    void /** @type {HTMLElement} */ (btn).offsetWidth;
    btn.classList.add('is-swapped-out');
    window.setTimeout(() => btn.classList.remove('is-swapped-out'), 500);
  }

  #syncAllPickRows() {
    for (const row of this.querySelectorAll('[data-bundle-pick]')) {
      this.#writePick(row, this.#readPick(row), true);
    }
  }

  /**
   * Mirror the main product variant picker into the bundle dropdowns.
   * Only updates slots the user hasn't manually overridden.
   * @param {CustomEvent} event
   */
  /**
   * ⚠️ THE PILL CLOBBERS US. <t1-stock-alert> has its own variant:update listener
   * (assets/t1-rwe-stock-alert.js) that morphs its children out of the fetched document
   * — the server copy, which says "this variant" and knows only slot 1. It lands AFTER
   * our line, so our text survives right up until the 774 KB fetch resolves and is then
   * silently replaced. Listener order is not something we can rely on, so re-render on
   * the next frame after the event instead of racing it.
   */
  /**
   * ⚠️ sticky-add-to-cart.js morphs the ENTIRE bar from server HTML on every variant change,
   * which throws away whatever we painted into it. Same failure and same remedy as the stock
   * line below: repaint on the far side of the morph.
   */
  #restoreStickyPile = () => {
    requestAnimationFrame(() => requestAnimationFrame(() => this.#renderStickyPile()));
  };

  #restoreStockLine = () => {
    requestAnimationFrame(() => requestAnimationFrame(() => this.#renderStockLine()));
  };

  #onMainVariantUpdate = (event) => {
    const variantId = Number(event?.detail?.resource?.id);
    if (!variantId || !this.#variantById.has(variantId)) return;

    // Skip mirroring when this variant:update was triggered by a bundle SLOT
    // change (we only wanted the featured media to follow). Genuine changes to
    // the MAIN picker still mirror into untouched slots.
    if (this.#suppressSlotMirror) {
      this.#suppressSlotMirror = false;
    } else {
      // P7 / Loom 4: slot #1 mirrors the picker, and slots 2..N are RE-RESOLVED
      // from the candidate list so a character promoted into slot #1 is never
      // left sitting in another slot too. Mirroring the same id into every slot
      // (what this used to do) is exactly the duplicate we are removing.
      this.#applyAllSlotDefaults(variantId);
    }

    this.#renderAllPrices();
    this.#updateAddToCartText();
    this.#updateAddToCartState();
    this.#broadcastSubtotal();
  };

  /**
   * Drive the MAIN product variant picker to the given variant so Horizon's
   * native variant:update flow runs and swaps the featured image (and price,
   * sku, etc). We resolve the variant's option values, set the matching option
   * control in each fieldset/select, then dispatch the same 'change' the picker
   * already listens for. We add no fetch logic and touch nothing in the bundle
   * DOM — the picker morphs only itself and the media gallery replaces only
   * itself, so bundle selections survive untouched.
   * @param {number} variantId
   * @returns {boolean} true if the picker moved and dispatched a change (a
   *   variant:update will follow); false if nothing changed.
   */
  /**
   * Move the gallery to a variant's image INSTANTLY, with no network at all.
   *
   * ⚠️ THE SLOWNESS WAS NEVER THE GALLERY — it was waiting on a 774,134-byte fetch.
   * Horizon's variant-picker.js requests the WHOLE product page on every variant change
   * (`/products/<handle>?option_values=…`, no section_id), DOMParser's it, then morphs.
   * Measured on this product: 774 KB per tap. The image cannot appear until that lands.
   *
   * But all 18 product images are ALREADY in the DOM as <slideshow-slide>, and
   * <slideshow-component> exposes a public select(index). So the picture is a local
   * operation — the same reason drinkmagna.com feels instant. We do it first, on the
   * click, and let the fetch continue in the background for price/stock/ATC.
   *
   * ⚠️ Matched on the image FILENAME, not the full URL: the gallery renders its own
   * width/crop params, so the src strings never match end to end.
   * @param {number} variantId
   * @returns {boolean} true if a slide matched and was selected
   */
  #showVariantMedia(variantId) {
    const v = this.#variantById.get(Number(variantId));
    const src = v && (v.featured_image || '');
    if (!src) return false;
    const file = String(src).split('?')[0].split('/').pop();
    if (!file) return false;
    const gallery = document.querySelector('media-gallery');
    const show = gallery && gallery.querySelector('slideshow-component');
    if (!show || typeof show.select !== 'function') return false;
    const slides = Array.from(gallery.querySelectorAll('slideshow-slide'));
    const i = slides.findIndex((s) => {
      const img = s.querySelector('img');
      const cur = img && (img.currentSrc || img.getAttribute('src') || '');
      return cur && cur.split('?')[0].split('/').pop() === file;
    });
    if (i < 0) return false;
    show.select(i);
    return true;
  }

  #syncMainPickerFromVariant(variantId) {
    const variant = this.#variantById.get(Number(variantId));
    if (!variant) return false;

    const section = this.closest('.shopify-section') || this.closest('section') || document;
    const picker =
      section.querySelector('variant-picker[data-template-product-match="true"]') ||
      section.querySelector('variant-picker');
    // No picker rendered (product has only a default variant) — nothing to sync.
    if (!picker) return false;

    const optionValues = [variant.option1, variant.option2, variant.option3];

    /** @type {HTMLElement | null} */
    let lastChanged = null;

    for (let i = 0; i < optionValues.length; i++) {
      const value = optionValues[i];
      if (value == null || value === '') continue;

      // Buttons / swatches render as a <fieldset> keyed by the option index.
      const fieldset = picker.querySelector(`fieldset[data-fieldset-index="${i}"]`);
      if (fieldset) {
        const radios = /** @type {NodeListOf<HTMLInputElement>} */ (
          fieldset.querySelectorAll('input[type="radio"]')
        );
        for (const radio of radios) {
          if (radio.value === value) {
            if (!radio.checked) {
              radio.checked = true;
              lastChanged = radio;
            }
            break;
          }
        }
        continue;
      }

      // Dropdowns render as a <select> whose id ends with the option index.
      // The picker reads selections via `option[selected]`, so set the attribute
      // (not just .value) for every select we move, otherwise its request URL
      // would carry a stale option value.
      const selects = /** @type {NodeListOf<HTMLSelectElement>} */ (picker.querySelectorAll('select'));
      for (const select of selects) {
        if ((select.id.split('-').pop() || '') !== String(i)) continue;
        const option = Array.from(select.options).find((o) => o.value === value);
        if (option && !option.selected) {
          for (const o of select.options) o.removeAttribute('selected');
          option.setAttribute('selected', 'selected');
          select.value = value;
          lastChanged = select;
        }
        break;
      }
    }

    // Nothing moved — the main picker already shows this variant. Skip the
    // dispatch so we don't fire a redundant section fetch.
    if (!lastChanged) return false;

    lastChanged.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  // -----------------------------------------------------------------
  // P7 — slot defaults
  // -----------------------------------------------------------------

  /**
   * The slot-default sequence for a given "slot #1" variant: that variant
   * first, then every other in-stock candidate in rank order. Slot i takes
   * seq[i % seq.length], so a tier with more slots than in-stock variants
   * (Buy-3-Get-1 is quantity 4) wraps instead of repeating one character.
   *
   * ⚠️ This reimplements the Liquid in blocks/bundle-group.liquid exactly. If
   * the two ever disagree, the first variant:update visibly reshuffles the tier
   * the shopper is already looking at.
   * @param {number|null} firstId
   * @returns {number[]}
   */
  #resolveSequence(firstId) {
    const candidates = this.#candidates;
    if (!candidates.length) return [];
    const wanted = Number(firstId);
    const first = candidates.includes(wanted) ? wanted : candidates[0];
    return [first, ...candidates.filter((id) => id !== first)];
  }

  /**
   * Re-resolve every auto slot in one tier from the candidate sequence.
   * Slot #1 takes the main picker's variant; slots 2..N take the next UNUSED
   * candidates, which is what drops a collision when the shopper switches the
   * picker to a character already sitting further down the tier.
   *
   * ⚠️ Slots the shopper hand-picked carry data-user-set and are never moved.
   * Their ids are pre-registered as used so the auto slots route around them.
   * @param {Element} tier
   * @param {number|null} firstId
   */
  #applyTierSlotDefaults(tier, firstId) {
    // P8: a tier rendered as a pick row owns its own slots.
    const row = this.#pickRowOf(tier);
    if (row) {
      this.#applyPickRowDefaults(row, firstId);
      return;
    }

    const seq = this.#resolveSequence(firstId);
    if (!seq.length) return;

    const selects = /** @type {(HTMLSelectElement|HTMLInputElement)[]} */ (
      Array.from(tier.querySelectorAll('[data-bundle-variant-select]'))
    );
    if (!selects.length) return;

    /** @type {Set<number>} */
    const used = new Set();
    for (const sel of selects) {
      if (sel.dataset.userSet !== 'true') continue;
      const value = Number(sel.value);
      if (value) used.add(value);
    }

    let pointer = 0;
    selects.forEach((sel, index) => {
      if (sel.dataset.userSet === 'true') return;
      let pick;
      if (index === 0) {
        pick = seq[0];
      } else {
        while (pointer < seq.length && used.has(seq[pointer])) pointer++;
        pick = pointer < seq.length ? seq[pointer++] : seq[index % seq.length];
      }
      used.add(pick);
      this.#setSlotValue(sel, pick);
    });
  }

  /**
   * P8 — re-resolve a pick row when the MAIN picker moves.
   *
   * Untouched row: recompute the whole selection from the candidate sequence,
   * which is byte-for-byte P7's rule — picker variant first, then the next best
   * sellers, no duplicates.
   *
   * Touched row: the shopper has curated it, so never reshuffle. Only guarantee
   * their own picker variant is present, replacing the least-recently added
   * pick when it is not. This is the pick-row equivalent of the data-user-set
   * exemption on the <select> path.
   *
   * @param {Element} row
   * @param {number|null} firstId
   */
  /**
   * Paint the sticky bar's character pile + short-name line from the ACTIVE tier's picks.
   *
   * Chris, 2026-09-10: "the sticky ATC doesn't make sense, it only features one of the many
   * items selected… people won't understand that they have the pack of 4 selected and this
   * will lead to some dropoff." The bar is the last thing a shopper reads before tapping buy,
   * so showing one figure while four are in the bundle is the worst possible place to be
   * ambiguous.
   *
   * ⚠️ Duplicates are REPEATED rather than collapsed to a x2 chip. Four slots should look
   * like four things; a single avatar with a badge reads as "one item" at a glance, which is
   * the exact misunderstanding being fixed. The names line does the collapsing instead,
   * where "Fox x2" is unambiguous in words.
   */
  #renderStickyPile() {
    const pile = document.querySelector('[data-sticky-pile]');
    const names = document.querySelector('[data-sticky-pile-names]');
    const count = document.querySelector('[data-sticky-pile-count]');
    if (!pile) return;

    const tier = this.#getActiveTier();
    const row = tier && this.#pickRowOf(tier);
    const picks = row ? this.#readPick(row) : [];

    // ⚠️ The pile renders from ONE pick upward — the theme's thumbnail uses the product /
    // variant photo, and Chris wants the swatch crop we built instead. The COPY is what stays
    // unchanged at one pick; that is gated separately, on the count line below.
    if (picks.length < 1) {
      // One pick keeps the theme's existing copy — Chris: "if they only pick 1, keep the
      // copy as is". The product title and variant name come back with the thumbnail.
      pile.hidden = true;
      pile.replaceChildren();
      if (names) { names.hidden = true; names.textContent = ''; }
      if (count) { count.hidden = true; count.textContent = ''; }
      return;
    }

    const MAX = 4;
    const shown = picks.slice(0, MAX);
    const extra = picks.length - shown.length;

    const frag = document.createDocumentFragment();
    shown.forEach((id, i) => {
      const v = this.#variantById.get(Number(id));
      const item = document.createElement('span');
      item.className = 'sticky-pile__item';
      // Leftmost on top, so the pile reads in pick order.
      item.style.setProperty('--pile-z', String(shown.length + 1 - i));
      const src = v && (v.swatch_image || v.featured_image);
      if (src) {
        const img = document.createElement('img');
        img.className = 'sticky-pile__img';
        img.src = String(src);
        img.width = 96; img.height = 96;
        img.loading = 'lazy'; img.decoding = 'async'; img.alt = '';
        item.appendChild(img);
      }
      frag.appendChild(item);
    });

    if (extra > 0) {
      const item = document.createElement('span');
      item.className = 'sticky-pile__item';
      item.style.setProperty('--pile-z', '0');
      const chip = document.createElement('span');
      chip.className = 'sticky-pile__more';
      chip.textContent = `+${extra}`;
      item.appendChild(chip);
      frag.appendChild(item);
    }

    pile.replaceChildren(frag);
    pile.hidden = false;

    // ── COPY ─────────────────────────────────────────────────────────────────
    // Chris's format (2026-09-10): a count line, then the characters.
    //     4 Figures
    //     Bunny, Bear, Shadow Bear, Golden Bear
    // He flagged the risk himself — "this might be a flawed vision because of the text
    // length" — and he is right: at 390px the pile takes ~90px and the names have ~150px
    // left, so the full list cannot fit.
    //
    // ⚠️ The fix is NOT a CSS ellipsis. `Bunny, Bear, Shadow Be…` truncates mid-word and
    // reads as a rendering fault rather than as information. Instead the list is TRIMMED TO
    // FIT and the remainder is stated: `Bunny, Bear +2`. Nothing is ever cut mid-word, the
    // count line already carries the total, and the pile shows the faces — so the shopper
    // always knows how many and can always see them, at any width.
    // ⚠️ Name the PRODUCT, not the category. Chris, 2026-09-10: "instead of saying 4 Figures,
    // go for 4 Nightmare Animatronics (or whatever the products and amounts they have in their
    // cart would be)." "4 Figures" is true of every product on the store; "4 Nightmare
    // Animatronics" tells the shopper what is actually in the bundle. Title comes from
    // data-product-title on the bundle root, so it needs no lookup and no fetch.
    // At one pick the count and names stay hidden and the theme's own copy stands.
    if (count) {
      const multi = picks.length > 1;
      const productTitle = this.dataset.productTitle || 'Figures';
      count.textContent = multi ? `${picks.length} ${productTitle}` : '';
      count.hidden = !multi;
    }
    if (picks.length < 2) {
      if (names) { names.hidden = true; names.textContent = ''; }
      return;
    }

    if (names) {
      /** @type {Map<number, number>} */
      const counts = new Map();
      for (const id of picks) counts.set(Number(id), (counts.get(Number(id)) || 0) + 1);
      const parts = [...counts.entries()].map(([id, n]) => {
        const v = this.#variantById.get(id);
        const nm = (v && (v.short_name || v.title)) || '';
        return nm ? (n > 1 ? `${nm} \u00d7${n}` : nm) : '';
      }).filter(Boolean);

      names.hidden = !parts.length;
      names.textContent = parts.join(', ');

      // Drop names from the end until the line fits, then say how many were dropped.
      // Measured against the element's own box, so it adapts to any width without a
      // breakpoint. Bounded by parts.length, so it always terminates.
      for (let keep = parts.length; keep > 1 && names.scrollWidth > names.clientWidth; keep--) {
        const hidden = parts.length - (keep - 1);
        names.textContent = `${parts.slice(0, keep - 1).join(', ')} +${hidden}`;
      }
    }
  }

  /** Is this character still selected on ANY tier? @param {number} id */
  #anyRowHolds(id) {
    for (const row of this.querySelectorAll('[data-bundle-pick]')) {
      if (this.#readPick(row).includes(Number(id))) return true;
    }
    return false;
  }

  /**
   * Fill a row from the cross-tier pick memory, topped up with best sellers.
   * Recent picks come first (they are the shopper's own choices), then the ranked
   * candidates fill any remaining slots, then the pool wraps if the tier holds more slots
   * than there are in-stock characters.
   * @param {Element} row
   */
  #seedRowFromRecent(row) {
    const capacity = this.#pickCapacity(row);
    const offered = this.#renderedIds(row);
    if (!offered.length || !capacity) return;
    const offeredSet = new Set(offered);

    const recent = this.#recentPicks.filter((id) => offeredSet.has(id));
    const sellers = this.#resolveSequence(recent[0] ?? null).filter((id) => offeredSet.has(id));

    /** @type {number[]} */
    const seed = [];
    for (const id of [...recent, ...sellers]) {
      if (seed.length >= capacity) break;
      if (!seed.includes(id)) seed.push(id);
    }
    // A Buy-3-Get-1 tier wants 4 slots; a product may offer fewer characters. Wrap rather
    // than leaving the tier short, which would block add-to-cart.
    const pool = seed.length ? seed.slice() : offered.slice();
    for (let i = 0; seed.length < capacity; i++) seed.push(pool[i % pool.length]);

    // Stored oldest-first so a swap evicts the LEAST recent character first and never the
    // one the shopper just chose.
    this.#writePick(row, seed.slice().reverse());
  }

  /** The variant the main picker currently holds, as a fallback seed before any tap. */
  #currentPickerVariantId() {
    const checked = document.querySelector(
      'variant-picker input[type="radio"]:checked, .variant-option input[type="radio"]:checked'
    );
    const id = Number(checked?.getAttribute('data-variant-id') || 0);
    if (id && this.#variantById.has(id)) return id;
    const first = this.#candidates?.[0];
    return first ? Number(first) : null;
  }

  #applyPickRowDefaults(row, firstId) {
    const capacity = this.#pickCapacity(row);
    const offered = this.#renderedIds(row);
    if (!offered.length) return;
    const offeredSet = new Set(offered);

    if (/** @type {HTMLElement} */ (row).dataset.userTouched === 'true') {
      const wanted = Number(firstId);
      if (!offeredSet.has(wanted)) return;
      const order = this.#readPick(row);
      if (order.includes(wanted)) return;
      if (order.length >= capacity) order.shift();
      order.push(wanted);
      this.#writePick(row, order);
      return;
    }

    const pool = this.#resolveSequence(firstId).filter((id) => offeredSet.has(id));
    if (!pool.length) return;
    /** @type {number[]} */
    const next = [];
    for (let i = 0; i < capacity; i++) next.push(pool[i % pool.length]);
    // Stored oldest-first REVERSED, so the first character a swap evicts is the
    // weakest default and never the shopper's own picker variant (pool[0]).
    this.#writePick(row, next.slice().reverse());
  }

  /** @param {number|null} firstId */
  #applyAllSlotDefaults(firstId) {
    for (const tier of this.querySelectorAll('[data-bundle-item]')) {
      this.#applyTierSlotDefaults(tier, firstId);
    }
  }

  /**
   * Move one slot, refusing to land on a sold-out option. Candidates are
   * already filtered to in-stock, so this is belt-and-braces against a stale
   * candidate list surviving a section morph.
   * @param {HTMLSelectElement|HTMLInputElement} sel
   * @param {number} id
   */
  #setSlotValue(sel, id) {
    const value = String(id);
    if (sel instanceof HTMLSelectElement) {
      const option = Array.from(sel.options).find((o) => o.value === value);
      if (!option || option.disabled) return;
      sel.value = value;
    } else if (sel instanceof HTMLInputElement) {
      sel.value = value;
    }
  }

  // -----------------------------------------------------------------
  // Selection helpers
  // -----------------------------------------------------------------

  /**
   * Public accessor used by assets/bundle-cart.js to read the active tier's
   * chosen variant ids at add-to-cart time. Returns null when no tier is
   * active so the caller can fall back to the plain product variant.
   * @returns {number[] | null}
   */
  getActiveSelections() {
    const tier = this.#getActiveTier();
    if (!tier) return null;
    const ids = this.#getTierVariantIds(tier);
    return ids.length ? ids : null;
  }

  #ensureSelectedTier() {
    const radios = /** @type {NodeListOf<HTMLInputElement>} */ (
      this.querySelectorAll('[data-bundle-radio]')
    );
    if (radios.length === 0) return;
    const anyChecked = Array.from(radios).some((r) => r.checked);
    if (!anyChecked) radios[0].checked = true;
  }

  /** @returns {HTMLInputElement | null} */
  #getCheckedRadio() {
    return /** @type {HTMLInputElement | null} */ (
      this.querySelector('[data-bundle-radio]:checked')
    );
  }

  /** @returns {HTMLElement | null} */
  #getActiveTier() {
    const radio = this.#getCheckedRadio();
    return radio ? /** @type {HTMLElement} */ (radio.closest('[data-bundle-item]')) : null;
  }

  /** @param {HTMLElement | null} tier */
  #isTierActive(tier) {
    if (!tier) return false;
    const radio = /** @type {HTMLInputElement | null} */ (tier.querySelector('[data-bundle-radio]'));
    return !!radio?.checked;
  }

  #syncSelectedClass() {
    const tiers = this.querySelectorAll('[data-bundle-item]');
    for (const tier of tiers) {
      const radio = /** @type {HTMLInputElement | null} */ (tier.querySelector('[data-bundle-radio]'));
      tier.classList.toggle('is-selected', !!radio?.checked);
    }
  }

  // -----------------------------------------------------------------
  // Variant id extraction
  // -----------------------------------------------------------------

  /**
   * For a given tier, returns the variant ids the user has chosen for each slot.
   * Falls back to the product's currently selected variant for unset slots
   * (e.g. when a tier's variant selectors are hidden because show_variant_selectors
   * is off — typical for the "Buy 1" single-item tier).
   * @param {HTMLElement} tier
   * @returns {number[]}
   */
  #getTierVariantIds(tier) {
    const quantity = Number(tier.dataset.bundleQuantity) || 1;
    const selects = /** @type {NodeListOf<HTMLSelectElement|HTMLInputElement>} */ (
      tier.querySelectorAll('[data-bundle-variant-select]')
    );

    /** @type {number[]} */
    const ids = [];
    for (const sel of selects) {
      const value = Number(sel.value);
      if (value && this.#variantById.has(value)) ids.push(value);
    }

    if (ids.length === quantity) return ids;

    const fallback = this.#getMainVariantId();

    // A tier with NO variant selectors at all (typically "Buy 1", where
    // show_variant_selectors is off) must stay on the shopper's own variant.
    // Substituting a different character there would be a silent swap.
    if (!selects.length) {
      if (!fallback) return ids;
      while (ids.length < quantity) ids.push(fallback);
      return ids;
    }

    // P7: a tier that HAS slots but came up short is padded from the candidate
    // list, not by repeating the main variant — repeating it is precisely the
    // duplicate this phase removes, and it would flatten good defaults at cart
    // time even when the UI looked right.
    const seq = this.#resolveSequence(fallback);
    if (seq.length) {
      const used = new Set(ids);
      let pointer = 0;
      while (ids.length < quantity) {
        while (pointer < seq.length && used.has(seq[pointer])) pointer++;
        if (pointer < seq.length) {
          const pick = seq[pointer++];
          used.add(pick);
          ids.push(pick);
        } else {
          ids.push(seq[ids.length % seq.length]);
        }
      }
      return ids;
    }

    // Nothing in stock — legacy behaviour so the cart path never breaks.
    if (!fallback) return ids;
    while (ids.length < quantity) ids.push(fallback);
    return ids;
  }

  /** @returns {number | null} */
  #getMainVariantId() {
    if (!this.#form) {
      const stored = /** @type {HTMLInputElement | null} */ (
        this.querySelector('[data-bundle-active-variant]')
      );
      return stored ? Number(stored.value) || null : null;
    }
    const input = /** @type {HTMLInputElement | null} */ (this.#form.querySelector('input[name="id"]'));
    const v = input ? Number(input.value) : NaN;
    return Number.isFinite(v) && v > 0 ? v : null;
  }

  // -----------------------------------------------------------------
  // Pricing
  // -----------------------------------------------------------------

  /** @param {HTMLElement} tier */
  #computeTierPricing(tier) {
    const discount = Number(tier.dataset.bundleDiscount) || 0;
    const factor = (100 - discount) / 100;
    // Optional flat currency discount (in dollars). When > 0 it overrides the
    // percentage: a fixed amount comes off the bundle total, mirroring how the
    // percentage applies to the whole-bundle total. Falls back to the existing
    // percentage path when unset/zero, so tiers without it are unchanged.
    const fixedAmount = Number(tier.dataset.bundleFixedAmount) || 0;

    const variantIds = this.#getTierVariantIds(tier);

    let totalPrice = 0;
    let totalCompare = 0;

    for (const id of variantIds) {
      const v = this.#variantById.get(id);
      if (!v) continue;
      totalPrice += Number(v.price) || 0;
      const compare = Number(v.compare_at_price) || 0;
      totalCompare += compare > Number(v.price) ? compare : Number(v.price) || 0;
    }

    const currentCents =
      fixedAmount > 0
        ? Math.max(0, totalPrice - Math.round(fixedAmount * 100))
        : Math.round(totalPrice * factor);
    // Guarantee compare >= current so the strike-through never looks broken.
    const compareCents = Math.max(totalCompare, totalPrice);
    const savingsCents = Math.max(0, compareCents - currentCents);

    return { currentCents, compareCents, savingsCents };
  }

  /** @param {Element} tier */
  #renderTierPrice(tier) {
    const { currentCents, compareCents, savingsCents } = this.#computeTierPricing(/** @type {HTMLElement} */ (tier));

    const currentEl = tier.querySelector('[data-bundle-price-current]');
    const compareEl = tier.querySelector('[data-bundle-price-compare]');
    const saveEl = tier.querySelector('[data-bundle-price-save]');

    if (currentEl) currentEl.textContent = this.#formatMoney(currentCents);

    if (compareEl) {
      if (compareCents > currentCents) {
        compareEl.textContent = this.#formatMoney(compareCents);
        compareEl.removeAttribute('hidden');
      } else {
        compareEl.textContent = '';
        compareEl.setAttribute('hidden', '');
      }
    }

    if (saveEl) {
      // ⚠️ A PERCENTAGE, not a money amount — this node is now the SAVE X% pill.
      // The raw label (with its `[percent]` placeholder) is handed over in
      // data-save-template by the Liquid, so we substitute rather than guess which
      // number in the string to swap, and the merchant's wording/language survives.
      const pct = compareCents > 0 ? Math.round((savingsCents / compareCents) * 100) : 0;
      if (pct > 0) {
        const tpl = saveEl.dataset.saveTemplate || 'Save [percent]%';
        const label = tpl.replace('[percent]', String(pct));
        saveEl.textContent = label;
        saveEl.setAttribute('aria-label', label);
        saveEl.removeAttribute('hidden');
      } else {
        saveEl.setAttribute('hidden', '');
      }
    }
  }

  /**
   * Flag every tier that costs MORE than the one currently selected, so the CSS can
   * run a shine sweep across its SAVE X% badge and pull the eye upward.
   * ⚠️ Recomputed on every broadcast, not just on load — the comparison is against the
   * CURRENT selection, so it has to invert the moment the shopper moves up a tier. A tier
   * that is selected is by definition never marked, which is why the shine only ever has
   * to be designed against the unselected (near-black) pill.
   */
  #markUpsellTiers() {
    const active = this.#getActiveTier();
    if (!active) return;
    const { currentCents: activeCents } = this.#computeTierPricing(active);
    for (const tier of this.querySelectorAll('[data-bundle-item]')) {
      const { currentCents } = this.#computeTierPricing(/** @type {HTMLElement} */ (tier));
      tier.classList.toggle('bundle-tier--upsell', currentCents > activeCents);
    }
  }

  #renderAllPrices() {
    const tiers = this.querySelectorAll('[data-bundle-item]');
    for (const tier of tiers) this.#renderTierPrice(tier);
  }

  /** @param {number} cents */
  #formatMoney(cents) {
    try {
      return formatMoney(cents, this.#moneyFormat, this.#currency);
    } catch {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  // -----------------------------------------------------------------
  // Add-to-cart button — append the bundle's current price.
  // -----------------------------------------------------------------

  #updateAddToCartText() {
    if (!this.#form) return;

    const tier = this.#getActiveTier();
    if (!tier) return;

    const { currentCents } = this.#computeTierPricing(tier);
    const priceText = this.#formatMoney(currentCents);

    const btn = this.#form.querySelector('add-to-cart-component button');
    if (!btn) return;

    // The default markup is: <span class="svg-wrapper">…</span><span>Add to cart</span>
    const textNode = btn.querySelector('span:not(.svg-wrapper)');
    if (!textNode) return;

    if (!textNode.dataset.bundleBaseText) {
      textNode.dataset.bundleBaseText = (textNode.textContent || '').trim() || 'Add to cart';
    }
    const base = textNode.dataset.bundleBaseText;
    textNode.textContent = `${base} — ${priceText}`;
  }

  /**
   * P8 — a half-filled bundle can never reach the cart.
   *
   * ⚠️ Only ever LIFTS a block this component applied (data-bundle-incomplete),
   * and never applies one over a button Horizon already disabled. Otherwise a
   * genuinely sold-out product would get its add-to-cart re-enabled the moment
   * a pick row filled up.
   */
  #updateAddToCartState() {
    if (!this.#form) return;
    const btn = /** @type {HTMLButtonElement|null} */ (
      this.#form.querySelector('add-to-cart-component button')
    );
    if (!btn) return;

    const tier = this.#getActiveTier();
    const row = tier ? this.#pickRowOf(tier) : null;
    const incomplete = !!row && this.#readPick(row).length < this.#pickCapacity(row);

    if (incomplete) {
      if (btn.disabled) return;
      btn.dataset.bundleIncomplete = 'true';
      btn.setAttribute('disabled', '');
      btn.setAttribute('aria-disabled', 'true');
      return;
    }

    if (btn.dataset.bundleIncomplete === 'true') {
      delete btn.dataset.bundleIncomplete;
      btn.removeAttribute('disabled');
      btn.removeAttribute('aria-disabled');
    }
  }

  // -----------------------------------------------------------------
  // Subtotal broadcast — drives offer unlock components in the section.
  // -----------------------------------------------------------------

  /**
   * Public hook so siblings (e.g. offer-unlock-group) that mount after the
   * bundle's first broadcast can request a fresh snapshot.
   */
  publishSubtotal() {
    this.#broadcastSubtotal();
  }

  #broadcastSubtotal() {
    const tier = this.#getActiveTier();
    if (!tier) return;

    // Every code path that changes the selection or the picks ends here, so this is the
    // one place the upsell flags are guaranteed to stay in sync.
    this.#markUpsellTiers();
    this.#renderStickyPile();

    const { currentCents, compareCents, savingsCents } = this.#computeTierPricing(tier);
    const detail = {
      sectionId: this.dataset.sectionId || null,
      productId: this.dataset.productId || null,
      bundleId: tier.dataset.bundleId || null,
      subtotalCents: currentCents,
      subtotal: currentCents / 100,
      formattedSubtotal: this.#formatMoney(currentCents),
      currency: this.#currency,
    };

    // Section-scoped event for offer components in the same product section.
    const section = this.closest('.shopify-section') || this.closest('section') || document;
    section.dispatchEvent(new CustomEvent('bundle:subtotal:update', { detail, bubbles: true }));
    // Document-scoped so future listeners (sticky bars, drawers) can hook in too.
    document.dispatchEvent(new CustomEvent('bundle:subtotal:update', { detail }));

    // Offer-group contract — same payload, normalized field names.
    // ⚠️ `totalPrice` is offer-group.js's contract (it reads detail.totalPrice) —
    // never rename or remove it. The compare/savings fields below are ADDITIVE,
    // consumed by <product-price> so the header price block follows the selected
    // bundle tier instead of freezing on the single-unit price.
    // ⚠️ Money is formatted HERE and nowhere else. The shop renders EUR with comma
    // decimals (€30,95); a second formatter in product-price.js would be a second
    // source of truth and would drift.
    const savingsPercent =
      compareCents > 0 ? Math.round((savingsCents / compareCents) * 100) : 0;
    document.dispatchEvent(
      new CustomEvent('titan:price:update', {
        detail: {
          sectionId: this.dataset.sectionId || null,
          totalPrice: currentCents,
          compareAtCents: compareCents,
          savingsCents,
          savingsPercent,
          onSale: savingsCents > 0 && compareCents > currentCents,
          formattedTotal: this.#formatMoney(currentCents),
          formattedCompareAt: this.#formatMoney(compareCents),
          formattedSavings: this.#formatMoney(savingsCents),
        },
      })
    );
  }
}

if (!customElements.get('bundle-group-component')) {
  customElements.define('bundle-group-component', BundleGroupComponent);
}
